import React, { useState, useRef, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Info } from "lucide-react";

const DropdownInput = ({ usedFor, boxLabel, color="bg-white", value, currentValue="", onChange, data = [], mustFill = false, description = "", className = "", validationError = false, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Transform data to ensure consistent format
  const transformedData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      if (typeof item === 'string') {
        return { id: item, name: item };
      }
      return { id: item._id || item.id, name: item.name || item };
    });
  }, [data]);

  // Update inputValue when currentValue changes
  useEffect(() => {
    if (currentValue) {
      if (typeof currentValue === 'string') {
        setInputValue(currentValue);
      } else if (currentValue.name) {
        setInputValue(currentValue.name);
      }
    } else {
      setInputValue("");
    }
  }, [currentValue]);

  // Filter data based on input
  const getFilteredData = () => {
    if (!inputValue) return transformedData;
    
    const searchTerm = inputValue.toLowerCase();
    return transformedData.filter((item) => 
      item.name.toLowerCase().includes(searchTerm)
    );
  };

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        validateInput();
      }
    };
    
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [inputValue]);

  const validateInput = () => {
    if (!inputValue) {
      setInputValue("");
      onChange({ target: { value: "" } });
      return;
    }

    const matchingItem = transformedData.find(
      item => item.name.toLowerCase() === inputValue.toLowerCase()
    );
    
    if (matchingItem) {
      onChange({ target: { value: matchingItem.id } });
    } else if (value) {
      const selectedItem = transformedData.find(item => item.id === value);
      if (selectedItem) {
        setInputValue(selectedItem.name);
      } else {
        setInputValue("");
        onChange({ target: { value: "" } });
      }
    } else {
      setInputValue("");
      onChange({ target: { value: "" } });
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    const filteredData = getFilteredData();

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex(prev => {
          const newIndex = prev < filteredData.length - 1 ? prev + 1 : 0;
          return newIndex;
        });
        break;
        
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filteredData.length - 1;
          return newIndex;
        });
        break;
        
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredData.length) {
          const selectedItem = filteredData[activeIndex];
          setInputValue(selectedItem.name);
          onChange({ target: { value: selectedItem.id } });
          setIsOpen(false);
          setHasInteracted(true);
        }
        break;
        
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
        
      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (!isOpen) setIsOpen(true);
    
    const exactMatch = transformedData.find(
      item => item.name.toLowerCase() === e.target.value.toLowerCase()
    );
    
    if (exactMatch) {
      onChange({ target: { value: exactMatch.id } });
    } else {
      onChange({ target: { value: "" } });
    }
  };

  const handleItemSelect = (item) => {
    setInputValue(item.name);
    onChange({ target: { value: item.id } });
    setIsOpen(false);
    setHasInteracted(true);
  };

  const handleDropdownToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.getElementById(usedFor)?.focus();
    }
  };

  const showError = hasInteracted && validationError;
  const filteredData = getFilteredData();
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          id={usedFor}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onBlur={() => {
            setIsFocused(false);
            setHasInteracted(true);
          }}
          placeholder={`Select ${boxLabel}`}
          className={`w-full p-3 border ${
            showError
              ? "border-red-500 focus:border-red-500"
              : isFocused
              ? "border-gray-600"
              : "border-gray-400"
          } rounded-lg outline-none ${
            inputValue === "" ? "text-gray-300" : ""
          } transition-colors appearance-none ${color}`}
          {...props}
        />
        <label
          htmlFor={usedFor}
          className={`absolute -top-2 left-2 px-1 text-sm ${
            showError ? "text-red-500" : "text-gray-500"
          } ${color} transition-colors`}
        >
          {boxLabel}
          {!mustFill && (
            <span className="text-gray-400 ml-1 text-xs">(optional)</span>
          )}
        </label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <div 
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
          onClick={handleDropdownToggle}
        >
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div 
          ref={listRef}
          className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <div
                key={item.id}
                className={`p-2 cursor-pointer ${
                  activeIndex === index
                    ? "bg-gray-100 font-medium"
                    : value === item.id
                    ? "bg-gray-50 font-medium"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => handleItemSelect(item)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {item.name}
              </div>
            ))
          ) : (
            <div className="p-2 text-gray-500">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DropdownInput;